import { useEffect, useImperativeHandle, useState, forwardRef } from 'react';
import { colors } from '@yorukiri/ui/tokens';
import type { SlashCommandItem } from './extensions/SlashCommand';

export type SlashMenuHandle = {
  onKeyDown: (event: KeyboardEvent) => boolean;
};

type Props = {
  items: SlashCommandItem[];
  command: (item: SlashCommandItem) => void;
};

export const SlashMenu = forwardRef<SlashMenuHandle, Props>(function SlashMenu(
  { items, command },
  ref,
) {
  const [index, setIndex] = useState(0);

  useEffect(() => setIndex(0), [items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        setIndex((i) => (i + 1) % Math.max(items.length, 1));
        return true;
      }
      if (event.key === 'ArrowUp') {
        setIndex((i) => (i - 1 + Math.max(items.length, 1)) % Math.max(items.length, 1));
        return true;
      }
      if (event.key === 'Enter') {
        const item = items[index];
        if (item) command(item);
        return true;
      }
      return false;
    },
  }));

  if (!items.length) {
    return (
      <div style={containerStyle}>
        <div style={{ padding: '12px 14px', color: colors.ink3, fontSize: 13, fontStyle: 'italic' }}>
          No matches.
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {items.map((item, i) => (
        <button
          key={item.id}
          onMouseEnter={() => setIndex(i)}
          onMouseDown={(e) => {
            e.preventDefault();
            command(item);
          }}
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            padding: '8px 12px',
            background: i === index ? colors.accentSoft : 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'inherit',
            borderRadius: 6,
          }}
        >
          <div style={{ fontSize: 13, color: colors.ink, fontWeight: 500 }}>{item.title}</div>
          {item.hint && <div style={{ fontSize: 11, color: colors.ink3, marginTop: 2 }}>{item.hint}</div>}
        </button>
      ))}
    </div>
  );
});

const containerStyle: React.CSSProperties = {
  background: colors.surface,
  border: `1px solid ${colors.line2}`,
  borderRadius: 10,
  boxShadow: '0 12px 40px -12px rgba(17,17,20,0.14), 0 2px 8px rgba(17,17,20,0.04)',
  padding: 4,
  minWidth: 240,
  maxHeight: 360,
  overflowY: 'auto',
};
